import { MAX_AMOUNT, formatAriary, parseAmount } from './money'

/**
 * Les bornes de saisie, côté client — décision 2026-09-05 (Faly).
 *
 * ⚠️ Ce fichier REJOUE `api/app/models/concerns/bounds.rb`, il ne le remplace
 * pas. L'autorité est le serveur : lui seul voit ce qui arrive par
 * `sync/push`, où le JSON est fabriqué par le client et peut donc ignorer tout
 * ce qui suit. Ce qu'on gagne ici, c'est de le dire à l'utilisateur AVANT
 * l'envoi, plutôt que de lui laisser une ligne coincée dans sa file de
 * synchronisation qu'il ne verra jamais échouer.
 *
 * Toute modification se fait des DEUX côtés, en même temps.
 */

/** Douze chiffres, comme `Bounds::MAX_AMOUNT`. Défini dans `money.ts`. */
export const MAX_MONTANT = MAX_AMOUNT
export const MAX_NOM = 80
export const MAX_NOTE = 5_000
/**
 * Six caractères, pas huit (décision 2026-09-05, Faly).
 *
 * On parle d'un public qui tape sur un clavier de téléphone : un minimum trop
 * haut ne produit pas des mots de passe plus sûrs, il en produit d'écrits sur
 * un papier. L'administrateur, lui, reste à douze — il ouvre les comptes des
 * autres.
 */
export const MIN_MOT_DE_PASSE = 6
export const MAX_MOT_DE_PASSE = 72

/** Bornes de date, comme `Bounds.date_range`. */
export const MIN_DATE = '2000-01-01'
export function maxDate(): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() + 30)
  return date.toISOString().slice(0, 10)
}

/**
 * Ce qu'un champ de montant doit dire quand ça ne va pas — et rien quand tout
 * va bien.
 *
 * Un message et non un booléen : « invalide » n'aide personne à corriger. On
 * distingue donc « ce n'est pas un nombre » de « c'est trop grand », qui
 * n'appellent pas le même geste.
 */
export function messageMontant(
  saisie: string,
  { obligatoire = false }: { obligatoire?: boolean } = {},
): string | null {
  const brut = saisie.trim()
  if (brut === '') return obligatoire ? 'Indique un montant.' : null

  const montant = parseAmount(brut)
  // `parseAmount` refuse déjà le négatif (il n'accepte que des chiffres) : le
  // signe est porté par le sens de l'opération, jamais par le montant.
  if (montant === null) return 'Un montant s’écrit en chiffres, sans virgule.'
  if (montant > MAX_MONTANT) return `Montant trop grand (maximum ${formatAriary(MAX_MONTANT)}).`
  if (obligatoire && montant === 0) return 'Indique un montant.'
  return null
}

/**
 * Le motif d'un champ de montant, pour la validation NATIVE du navigateur.
 *
 * Chiffres et séparateurs de milliers seulement — les mêmes que tolère
 * `parseAmount`. Le champ reste un `type="text"` : `type="number"` ouvrirait
 * les flèches et le défilement, qui changent une valeur sans qu'on s'en
 * aperçoive.
 */
export const MOTIF_MONTANT = '[0-9  .,\u00a0\u202f]*'

/** Même principe pour un texte : la longueur est la seule chose à dire. */
export function messageTexte(
  saisie: string,
  maximum: number,
  { obligatoire = false, quoi = 'Ce champ' }: { obligatoire?: boolean; quoi?: string } = {},
): string | null {
  const brut = saisie.trim()
  if (brut === '') return obligatoire ? `${quoi} est obligatoire.` : null
  if (brut.length > maximum) return `${quoi} est trop long (maximum ${maximum} caractères).`
  return null
}

/*
 * ─── Téléphone et email ────────────────────────────────────────────────────
 *
 * Un numéro malgache s'écrit `03X XX XXX XX`, X allant de 2 à 9 : dix chiffres
 * en tout (décision 2026-09-05, Faly). Avant, le champ « téléphone ou email »
 * était un `type="text"` sans la moindre règle — il acceptait n'importe quoi,
 * et l'utilisateur ne l'apprenait qu'au refus du serveur, ou jamais.
 */

/** Les chiffres seuls, débarrassés de la mise en forme et du préfixe pays. */
export function chiffresDuTelephone(saisie: string): string {
  const brut = saisie.replace(/[\s.\-()]/g, '')
  return /^\+?261(\d{9})$/.exec(brut)?.[1]?.replace(/^/, '0') ?? brut
}

/**
 * Les préfixes mobiles malgaches en service — 032 à 039 (Faly, 2026-09-05).
 *
 * Écrit comme un intervalle de classe de caractères, pour servir aussi bien à
 * une `RegExp` qu'à l'attribut `pattern` d'un champ. Ce que ça ferme, c'est
 * `030` et `031`, qui n'existent pas — et tout ce qui ne commence pas par 03.
 *
 * ⚠️ À tenir à jour DES DEUX CÔTÉS avec `Account::PHONE_FORMAT` : le jour où
 * un opérateur ouvre un préfixe hors de cet intervalle, ses abonnés sont
 * bloqués à l'inscription tant que les deux ne l'ont pas appris.
 */
export const PREFIXES = '2-9'

/** Dix chiffres, `03` puis un préfixe d'opérateur — la seule forme acceptée. */
export function estTelephoneMalgache(saisie: string): boolean {
  return new RegExp(`^03[${PREFIXES}]\\d{7}$`).test(chiffresDuTelephone(saisie))
}

/**
 * Le même contrôle, pour l'attribut `pattern` d'un champ.
 *
 * Il porte sur la valeur AFFICHÉE, espaces compris, puisque le champ se met en
 * forme au fil de la frappe. Un `03[2348]\d{7}` nu échouerait sur
 * « 034 12 345 67 », que l'utilisateur voit pourtant comme correct.
 */
export const MOTIF_TELEPHONE = `03[${PREFIXES}] ?\\d{2} ?\\d{3} ?\\d{2}`

/** Téléphone OU email, pour un champ qui accepte les deux. */
export const MOTIF_IDENTIFIANT = `(${MOTIF_TELEPHONE})|([^@\\s]+@[^@\\s]+\\.[a-zA-Z]{2,})`

/** Ce que le navigateur affiche dans son infobulle de validation. */
export const AIDE_TELEPHONE =
  'Un numéro malgache commence par 03(2 à 9) et compte 10 chiffres.'
export const AIDE_IDENTIFIANT =
  'Indique un numéro malgache (03(2 à 9), 10 chiffres) ou une adresse email.'

/**
 * Met en forme au fil de la frappe : `034 12 345 67`.
 *
 * Les espaces sont posés par groupes de 3-2-3-2, et RIEN d'autre n'est touché :
 * ce qui n'est pas un chiffre est rendu tel quel, sinon on empêcherait de taper
 * une adresse email dans un champ qui accepte les deux.
 */
export function formaterTelephone(saisie: string): string {
  const chiffres = saisie.replace(/\D/g, '')
  if (chiffres === '') return ''

  const groupes = [
    chiffres.slice(0, 3), chiffres.slice(3, 5),
    chiffres.slice(5, 8), chiffres.slice(8, 10),
  ]
  return groupes.filter((g) => g !== '').join(' ')
}

/**
 * Ce qu'il faut afficher dans un champ qui accepte les DEUX.
 *
 * On ne met en forme que si la saisie ne contient aucune lettre : dès qu'il y
 * en a une, c'est une adresse email en cours de frappe, et y insérer des
 * espaces la rendrait intapable.
 */
export function formaterIdentifiant(saisie: string): string {
  if (/[^\d\s.\-()+]/.test(saisie)) return saisie
  // Le `+261` tapé à la main est ramené à la forme locale, la seule que
  // l'écran montre : deux écritures du même numéro sur le même champ.
  const chiffres = chiffresDuTelephone(saisie)
  return formaterTelephone(chiffres.slice(0, 10))
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i

export function estEmail(saisie: string): boolean {
  return EMAIL.test(saisie.trim())
}

/** Un identifiant de connexion : un numéro malgache OU une adresse email. */
export function messageIdentifiant(
  saisie: string, { obligatoire = false }: { obligatoire?: boolean } = {},
): string | null {
  const brut = saisie.trim()
  if (brut === '') return obligatoire ? 'Indique ton numéro ou ton email.' : null
  // La présence d'un `@` dit ce que l'utilisateur ESSAIE de taper : lui
  // répondre « ce n'est pas un numéro » quand il tape une adresse serait
  // exact et inutile.
  if (brut.includes('@')) {
    return estEmail(brut) ? null : 'Cette adresse email n’est pas valide.'
  }
  return estTelephoneMalgache(brut)
    ? null
    : AIDE_TELEPHONE
}

/** Un champ qui n'accepte QUE le téléphone — inscription, changement de numéro. */
export function messageTelephone(
  saisie: string, { obligatoire = false }: { obligatoire?: boolean } = {},
): string | null {
  const brut = saisie.trim()
  if (brut === '') return obligatoire ? 'Indique ton numéro.' : null
  return estTelephoneMalgache(brut) ? null : AIDE_TELEPHONE
}

export function messageEmail(
  saisie: string, { obligatoire = false }: { obligatoire?: boolean } = {},
): string | null {
  const brut = saisie.trim()
  if (brut === '') return obligatoire ? 'Indique ton email.' : null
  if (brut.length > 255) return 'Cette adresse est trop longue.'
  return estEmail(brut) ? null : 'Cette adresse email n’est pas valide.'
}

export function messageMotDePasse(
  saisie: string, { obligatoire = true }: { obligatoire?: boolean } = {},
): string | null {
  if (saisie === '') return obligatoire ? 'Choisis un mot de passe.' : null
  if (saisie.length < MIN_MOT_DE_PASSE) {
    return `Le mot de passe fait au moins ${MIN_MOT_DE_PASSE} caractères.`
  }
  if (saisie.length > MAX_MOT_DE_PASSE) {
    return `Le mot de passe fait au plus ${MAX_MOT_DE_PASSE} caractères.`
  }
  return null
}

/**
 * Une date que l'app sait manipuler.
 *
 * Ce qu'on ferme, c'est l'an 9999 tapé par erreur dans un champ date — pas un
 * usage légitime, d'où une fenêtre volontairement large.
 */
export function messageDate(
  saisie: string,
  { obligatoire = false }: { obligatoire?: boolean } = {},
): string | null {
  if (saisie === '') return obligatoire ? 'Indique une date.' : null
  if (!/^\d{4}-\d{2}-\d{2}$/.test(saisie)) return 'Cette date n’est pas lisible.'
  if (saisie < MIN_DATE) return 'Cette date est trop ancienne.'
  if (saisie > maxDate()) return 'Cette date est trop lointaine.'
  return null
}
